import { useState } from 'react';
import { ethers } from 'ethers';

const ERC20_ABI = [
  'function symbol() view returns (string)',
  'function name() view returns (string)',
  'function decimals() view returns (uint8)',
  'function totalSupply() view returns (uint256)'
];

/**
 * Hook to auto-detect token information from contract address
 * Fetches from DexScreener and CoinGecko for logo and price
 */
export const useTokenDetector = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFromDexScreener = async (address) => {
    try {
      const response = await fetch(
        `https://api.dexscreener.com/latest/dex/tokens/${address}`
      );
      const data = await response.json();
      
      if (data.pairs && data.pairs.length > 0) {
        const pair = data.pairs[0];
        return {
          logo: pair.info?.imageUrl || null,
          price: pair.priceUsd || null,
          priceChange24h: pair.priceChange?.h24 || 0,
          liquidity: pair.liquidity?.usd || 0
        };
      }
      return null;
    } catch (err) {
      console.warn('DexScreener fetch failed:', err);
      return null;
    }
  };

  const fetchFromCoinGecko = async (address) => {
    try {
      // CoinGecko API for BSC tokens
      const response = await fetch(
        `https://api.coingecko.com/api/v3/coins/binance-smart-chain/contract/${address.toLowerCase()}`
      );
      
      if (response.ok) {
        const data = await response.json();
        return {
          logo: data.image?.small || data.image?.thumb || null,
          price: data.market_data?.current_price?.usd || null,
          priceChange24h: data.market_data?.price_change_percentage_24h || 0
        };
      }
      return null;
    } catch (err) {
      console.warn('CoinGecko fetch failed:', err);
      return null;
    }
  };

  const fetchTokenFromContract = async (address, provider) => {
    try {
      const contract = new ethers.Contract(address, ERC20_ABI, provider);
      const [symbol, name, decimals] = await Promise.all([
        contract.symbol(),
        contract.name(),
        contract.decimals()
      ]);
      
      return { symbol, name, decimals: Number(decimals) };
    } catch (err) {
      throw new Error('Invalid token contract');
    }
  };

  const detectToken = async (address, provider) => {
    if (!ethers.isAddress(address)) {
      setError('Invalid address format');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Fetch basic info from contract
      const contractInfo = await fetchTokenFromContract(address, provider);
      
      // 2. Try to fetch logo and price from DexScreener first
      let externalData = await fetchFromDexScreener(address);
      
      // 3. If DexScreener fails, try CoinGecko
      if (!externalData || !externalData.logo) {
        const cgData = await fetchFromCoinGecko(address);
        if (cgData) {
          externalData = {
            ...externalData,
            logo: cgData.logo || externalData?.logo,
            price: cgData.price || externalData?.price,
            priceChange24h: cgData.priceChange24h || externalData?.priceChange24h || 0
          };
        }
      }

      // 4. Construct token object
      const token = {
        address: address,
        symbol: contractInfo.symbol,
        name: contractInfo.name,
        decimals: contractInfo.decimals,
        logo: externalData?.logo || `https://ui-avatars.com/api/?name=${contractInfo.symbol}&background=e50914&color=fff`,
        isCustom: true,
        price: externalData?.price || null,
        priceChange24h: externalData?.priceChange24h || 0,
        liquidity: externalData?.liquidity || 0
      };

      setLoading(false);
      return token;
    } catch (err) {
      console.error('Token detection error:', err);
      setError(err.message || 'Failed to detect token');
      setLoading(false);
      return null;
    }
  };

  return { detectToken, loading, error };
};
